import React, { useEffect, useRef, useState } from "react";
import {
  Map, MapPin, Gauge, Shield, Building2, ChevronUp, ChevronDown, Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import backendApi from "../../services/backendApi";

// Types
interface PlantInfo { name: string; fullName: string; description: string; capacity: string; mapEmbedUrl: string; coordinates: { lat: string; lng: string; location: string }; }
interface NavigationSection { id: string; label: string; }
interface Navigation { sections: NavigationSection[]; backNavigationRoute?: string; }
interface PlantOverviewCard { icon: keyof typeof iconMap; title: string; lines: string[]; featured?: boolean; isGreen?: boolean; }
interface PlantOverview { title: string; cards: PlantOverviewCard[]; }
interface TechnicalSpecifications { title: string; tableTitle: string; headers: string[]; data: string[][]; }
interface UIConfig { labels: { goTo: string }; tooltips: { lightMode: string; darkMode: string }; buttons: { backToNavigation: string }; }
interface Theme { primaryColor: string; storageKey?: string; }
interface SiteData { plantInfo: PlantInfo; navigation: Navigation; plantOverview: PlantOverview; technicalSpecifications: TechnicalSpecifications; ui: UIConfig; theme: Theme; }

const iconMap = { Map, MapPin, Gauge, Shield, Building2 };

const formatValue = (value: any, fallback = "Not Available") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  if (!text) return fallback;
  return text;
};

const toNumber = (value: any): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const L_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const L_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

const PLANT_LABELS: Record<number, string> = {
  0: "Solar", 1: "Wind", 2: "Hybrid", 3: "Battery-Hydro", 4: "Battery-Chem",
};
const PLANT_COLORS: Record<number, string> = {
  0: "#f59e0b", 1: "#10b981", 2: "#0084CF", 3: "#0084CF", 4: "#ef4444",
};
const getPlantLabel = (s: any) =>
  s.plant_code != null ? (PLANT_LABELS[s.plant_code] ?? s.site_type ?? "—") : (s.site_type ?? "—");

const getPlantColor = (s: any) =>
  s.plant_code != null ? (PLANT_COLORS[s.plant_code] ?? "#0084CF") : "#0084CF";

function fmtNum(v: any, dp = 2, unit = ""): string {
  if (v == null || v === "" || v === "null" || v === "undefined") return "—";
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  if (!isFinite(n)) return "—";
  return unit ? `${n.toFixed(dp)} ${unit}` : n.toFixed(dp);
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return v; }
}

const getMapEmbedUrl = (lat: any, lng: any, location: string, siteCode: string) => {
  const latNum = toNumber(lat);
  const lngNum = toNumber(lng);
  if (latNum !== null && lngNum !== null) {
    return `https://maps.google.com/maps?q=${latNum},${lngNum}&z=12&output=embed`;
  }
  const query = encodeURIComponent(location || siteCode);
  return `https://maps.google.com/maps?q=${query}&z=11&output=embed`;
};

const buildSiteDataFromApi = (siteRecord: any, siteCodeParam: string): SiteData => {
  const siteCode = formatValue(siteRecord?.site_code, siteCodeParam.toUpperCase());
  const siteName = formatValue(siteRecord?.site_name, siteCode);
  const location = formatValue(siteRecord?.location, "Not Available");
  const capacityMW = formatValue(siteRecord?.capacity_mw, "0");
  const capacityMWp = formatValue(siteRecord?.capacity_mwp, "0");
  const companyName = formatValue(siteRecord?.company_name, "Not Available");
  const companyCode = formatValue(siteRecord?.company_code, "-");
  const lat = formatValue(siteRecord?.latitude, "NA");
  const lng = formatValue(siteRecord?.longitude, "NA");
  const mapEmbedUrl = getMapEmbedUrl(siteRecord?.latitude, siteRecord?.longitude, location, siteCode);

  const cards: PlantOverviewCard[] = [
    {
      icon: "Building2",
      title: "Site Identity",
      lines: [
        `Site Code: ${siteCode}`,
        `Site Type: ${formatValue(siteRecord?.site_type)}`,
        `Status: ${siteRecord?.is_active === false ? "Inactive" : "Active"}`,
      ],
    },
    {
      icon: "Gauge",
      title: "Plant Capacity",
      lines: [
        `AC Capacity (MW): ${capacityMW}`,
        `DC Capacity (MWp): ${capacityMWp}`,
        `Data Resolution: ${formatValue(siteRecord?.data_resolution)}`,
      ],
    },
    {
      icon: "MapPin",
      title: "Location & Geo",
      lines: [
        `Location: ${location}`,
        `Latitude: ${lat}`,
        `Longitude: ${lng}`,
      ],
    },
    {
      icon: "Shield",
      title: "Commercial",
      lines: [
        `PPA Tariff: ${formatValue(siteRecord?.ppa_tariff)}`,
        `Currency: ${formatValue(siteRecord?.currency, "INR")}`,
        `Plant Code: ${formatValue(siteRecord?.plant_code)}`,
      ],
    },
    {
      icon: "Building2",
      title: "Company",
      lines: [
        `Company: ${companyName}`,
        `Company Code: ${companyCode}`,
        `Company ID: ${formatValue(siteRecord?.company_id)}`,
      ],
    },
    {
      icon: "Map",
      title: "Site Manager",
      lines: [
        `Name: ${formatValue(siteRecord?.site_manager_name)}`,
        `Email: ${formatValue(siteRecord?.site_manager_email)}`,
        `Phone: ${formatValue(siteRecord?.site_manager_phone_no)}`,
      ],
    },
  ];

  const specsRows: string[][] = [
    ["1", "Site Name", siteName, "Onboarded Data"],
    ["2", "Site Code", siteCode, "Onboarded Data"],
    ["3", "Company", companyName, companyCode],
    ["4", "Site Type", formatValue(siteRecord?.site_type), "Onboarded Data"],
    ["5", "Location", location, "Onboarded Data"],
    ["6", "Latitude", lat, "Onboarded Data"],
    ["7", "Longitude", lng, "Onboarded Data"],
    ["8", "Altitude", formatValue(siteRecord?.altitude), "Onboarded Data"],
    ["9", "AC Capacity (MW)", capacityMW, "Onboarded Data"],
    ["10", "DC Capacity (MWp)", capacityMWp, "Onboarded Data"],
    ["11", "PPA Tariff", formatValue(siteRecord?.ppa_tariff), "Onboarded Data"],
    ["12", "Currency", formatValue(siteRecord?.currency, "INR"), "Onboarded Data"],
    ["13", "Plant Code", formatValue(siteRecord?.plant_code), "Onboarded Data"],
    ["14", "Commissioning Date", formatValue(siteRecord?.plant_commissioning_date), "Onboarded Data"],
    ["15", "Local Time Zone", formatValue(siteRecord?.local_time_zone), "Onboarded Data"],
    ["16", "Timezone Preference", formatValue(siteRecord?.timezone_preference), "Onboarded Data"],
    ["17", "Data Resolution", formatValue(siteRecord?.data_resolution), "Onboarded Data"],
    ["18", "Manager Name", formatValue(siteRecord?.site_manager_name), "Onboarded Data"],
    ["19", "Manager Email", formatValue(siteRecord?.site_manager_email), "Onboarded Data"],
    ["20", "Manager Phone", formatValue(siteRecord?.site_manager_phone_no), "Onboarded Data"],
    ["21", "Description", formatValue(siteRecord?.description), "Onboarded Data"],
  ];

  return {
    plantInfo: {
      name: siteCode,
      fullName: siteName,
      description: formatValue(siteRecord?.description),
      capacity: `${capacityMW} MW`,
      mapEmbedUrl,
      coordinates: {
        lat,
        lng,
        location,
      },
    },
    navigation: {
      sections: [
        { id: "overview", label: "Overview" },
        { id: "specifications", label: "Specifications" },
      ],
      backNavigationRoute: `/navigate/${siteCode}`,
    },
    plantOverview: {
      title: "Site Overview",
      cards,
    },
    technicalSpecifications: {
      title: "Technical Specifications",
      tableTitle: "Onboarded Site Details",
      headers: ["S.No", "Parameter", "Value", "Source"],
      data: specsRows,
    },
    ui: {
      labels: { goTo: "Go To" },
      tooltips: { lightMode: "Light Mode", darkMode: "Dark Mode" },
      buttons: { backToNavigation: "Back to Navigation" },
    },
    theme: { primaryColor: "#0084CF", storageKey: `site-info-theme-${siteCode}` },
  };
};

// Clean, minimal motion variants
const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as any } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2, ease: "easeIn" as any } }
};

const tileVariants = {
  hidden: { opacity: 0, y: 4 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" as any } }
};

const SiteInfo: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();

  const [siteData, setSiteData] = useState<SiteData | null>(null);
  const [rawSite, setRawSite] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leafletOk, setLeafletOk] = useState(false);

  const refs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];
  const [page, setPage] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('site_info_active_page');
      if (stored !== null) {
        const val = parseInt(stored, 10);
        if (val === 0 || val === 1) return val;
      }
    } catch (e) {}
    return 0;
  });

  useEffect(() => {
    try {
      localStorage.setItem('site_info_active_page', String(page));
    } catch (e) {}
  }, [page]);

  const mapDivRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<any>(null);

  useEffect(() => {
    let mounted = true;
    const loadStyle = (url: string) => {
      if (document.querySelector(`link[href="${url}"]`)) return Promise.resolve();
      return new Promise<void>((resolve, reject) => {
        const link = document.createElement("link");
        link.rel = "stylesheet"; link.href = url;
        link.onload = () => resolve(); link.onerror = reject;
        document.head.appendChild(link);
      });
    };
    const loadScript = (url: string) => {
      const existing = document.querySelector(`script[src="${url}"]`);
      if (existing) {
        if ((window as any).L) return Promise.resolve();
        return new Promise<void>((resolve, reject) => {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", reject);
        });
      }
      return new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = url; script.async = true;
        script.onload = () => resolve(); script.onerror = reject;
        document.head.appendChild(script);
      });
    };
    Promise.all([loadStyle(L_CSS), loadScript(L_JS)])
      .then(() => { if (mounted) setLeafletOk(true); })
      .catch(console.warn);
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!leafletOk || !mapDivRef.current || !rawSite) return;
    const L = (window as any).L;
    if (!L) return;

    if (!leafletMap.current) {
      leafletMap.current = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true, attributionControl: false });
      
      // Google Maps Roadmap tiles
      const primaryLayer = L.tileLayer("https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", {
        maxZoom: 20,
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "Map data &copy; Google",
      });
      primaryLayer.addTo(leafletMap.current);
      
      setTimeout(() => {
        leafletMap.current?.invalidateSize();
      }, 200);
    }

    const map = leafletMap.current;
    
    // Clear existing markers if any
    map.eachLayer((layer: any) => {
      if (layer instanceof L.Marker) {
        map.removeLayer(layer);
      }
    });

    const lat = parseFloat(String(rawSite.latitude));
    const lng = parseFloat(String(rawSite.longitude));

    if (!isNaN(lat) && !isNaN(lng)) {
      const col = getPlantColor(rawSite);
      const iconHtml = `
        <div style="width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
          background:${col};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.3);
          display:flex;align-items:center;justify-content:center;">
          <span style="transform:rotate(45deg);font-size:12px;color:#fff;">⚡</span>
        </div>`;

      const popupHtml = `
        <div style="font-family:'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;min-width:170px;padding:2px 0">
          <div style="font-weight:800;font-size:13px;color:#0f172a;line-height:1.2">${rawSite.site_name}</div>
          <div style="font-size:11px;color:#64748b;margin:2px 0 6px 0">${getPlantLabel(rawSite)}</div>
          <div style="font-size:11px;color:#334155;display:grid;gap:2px">
            <div><b>Location:</b> ${rawSite.location ?? "—"}</div>
            <div><b>AC/DC:</b> ${fmtNum(rawSite.capacity_mw, 2, "MW")} / ${fmtNum(rawSite.capacity_mwp, 2, "MWp")}</div>
            <div><b>COD:</b> ${fmtDate(rawSite.plant_commissioning_date)}</div>
          </div>
        </div>`;

      const icon = L.divIcon({ html: iconHtml, className: "", iconSize: [32, 32], iconAnchor: [16, 32] });
      const marker = L.marker([lat, lng], { icon })
        .addTo(map)
        .bindPopup(popupHtml, { maxWidth: 230 });

      map.setView([20.5937, 78.9629], 4);
      
      setTimeout(() => {
        marker.openPopup();
      }, 500);
    } else {
      map.setView([20.5937, 78.9629], 4);
    }
  }, [leafletOk, rawSite]);

  useEffect(() => {
    const normalizedSite = site?.trim().toUpperCase() || "";

    if (!normalizedSite) {
      setError("Site code is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const loadSiteData = async () => {
      try {
        const sitesResponse = await backendApi.getSites(false, normalizedSite);
        if (!sitesResponse.success || !Array.isArray(sitesResponse.sites)) {
          throw new Error(sitesResponse.error || "Failed to fetch site details");
        }

        const matchedSite =
          sitesResponse.sites.find(
            (item: any) => String(item?.site_code || "").toUpperCase() === normalizedSite
          ) ||
          sitesResponse.sites.find(
            (item: any) => String(item?.site_name || "").toUpperCase() === normalizedSite
          ) ||
          null;

        if (!matchedSite) {
          setSiteData(null);
          setRawSite(null);
          setError(`No onboarded site found for ${normalizedSite}`);
          return;
        }

        setRawSite(matchedSite);
        setSiteData(buildSiteDataFromApi(matchedSite, normalizedSite));
      } catch (loadError: any) {
        console.error("Failed to load site info:", loadError);
        setSiteData(null);
        setRawSite(null);
        setError(loadError?.message || "Unable to load real site data");
      } finally {
        setLoading(false);
      }
    };

    void loadSiteData();
  }, [site]);

  // Smooth, controlled scrolling
  const goTo = (idx: number) => {
    refs[idx].current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest"
    });
    setPage(idx);
  };

  const next = () => goTo(Math.min(1, page + 1));
  const prev = () => goTo(Math.max(0, page - 1));

  // Refined scroll detection with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const onScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const scrollY = window.scrollY;
        const newPage = scrollY > window.innerHeight * 0.5 ? 1 : 0;
        if (newPage !== page) setPage(newPage);
      }, 50);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timeoutId);
    };
  }, [page]);

  // Scroll to the stored page once loading is complete
  useEffect(() => {
    if (!loading && refs[page]?.current) {
      const timer = setTimeout(() => {
        refs[page].current?.scrollIntoView({
          behavior: "auto",
          block: "start",
          inline: "nearest"
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-[#F2F8FC] via-white to-[#F2F8FC]">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#0066A3] mx-auto mb-4" />
          <p className="text-[#0D2747] font-medium">Loading {site}</p>
        </div>
      </div>
    );
  }

  if (error || !siteData) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-100">
        <div className="text-center max-w-md">
          <p className="text-red-900 font-medium mb-2">Unable to load site</p>
          <p className="text-red-600 text-sm mb-6">{error}</p>
          <button
            onClick={() => navigate(`/navigate/${site}`)}
            className="bg-[#0066A3] text-white px-6 py-2 hover:bg-[#1C4D88] transition-colors font-medium"
          >
            Back to Navigation
          </button>
        </div>
      </div>
    );
  }

  const { plantInfo, plantOverview, technicalSpecifications, ui, theme } = siteData;

  return (
    <div className="bg-gradient-to-br from-[#F2F8FC] via-white to-[#F2F8FC] site-info-page">
      {/* Clean header with brand colors */}
      <header className="fixed top-16 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#E3F0F9]">
        <div className="max-w-none mx-0 pl-20 pr-8">
          <div className="h-14 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-10 h-10 bg-gradient-to-br from-[#0084CF] to-[#0066A3] rounded-md flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">{plantInfo.fullName}</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{page + 1} / 2</span>
              <div data-tour="si-pager" className="flex border border-[#C2DFF1] rounded overflow-hidden">
                <button
                  onClick={prev}
                  aria-label="Previous page"
                  disabled={page === 0}
                  className="w-10 h-10 flex items-center justify-center bg-white disabled:opacity-30 hover:bg-[#F2F8FC] transition-colors"
                >
                  <ChevronUp className="w-4 h-4 text-[#0066A3]" />
                </button>
                <button
                  onClick={next}
                  aria-label="Next page"
                  disabled={page === 1}
                  className="w-10 h-10 flex items-center justify-center border-l border-[#C2DFF1] bg-white disabled:opacity-30 hover:bg-[#F2F8FC] transition-colors"
                >
                  <ChevronDown className="w-4 h-4 text-[#0066A3]" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content sections - no gaps, perfect fit */}
      <div className="pt-[120px] ml-5 mr-6">
        <AnimatePresence mode="wait">
          {/* PAGE 1: Map + Tiles */}
          {page === 0 && (
            <motion.section
              key="page-1"
              ref={refs[0]}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="h-[calc(100vh-120px)] flex"
            >
              {/* Map - smaller section */}
              <div className="w-[40%] bg-gradient-to-br from-[#F2F8FC] to-[#F2F8FC] flex flex-col">
                <div className="p-6 border-b border-[#E3F0F9] bg-white">
                  <h2 className="text-lg font-semibold text-gray-900">Location</h2>
                </div>
                <div className="flex-1 p-6">
                  <div data-tour="si-map" className="h-full rounded-lg overflow-hidden border border-[#C2DFF1] shadow-sm relative z-0">
                    <div ref={mapDivRef} className="h-full w-full relative z-0" style={{ minHeight: "250px" }} />
                  </div>
                </div>
                <div className="p-6 border-t border-[#E3F0F9] bg-white">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-2 bg-[#F2F8FC] rounded border border-[#E3F0F9]">
                      <span className="text-sm text-gray-600">Location</span>
                      <span className="text-sm font-medium text-gray-900">{plantInfo.coordinates.location}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-[#F2F8FC] rounded border border-[#E3F0F9]">
                      <span className="text-sm text-gray-600">Coordinates</span>
                      <span className="text-sm font-medium text-gray-900">{plantInfo.coordinates.lat}, {plantInfo.coordinates.lng}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tiles - larger section with 3 per row */}
              <div className="w-[60%] bg-white">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
                  <p className="text-sm text-[#0066A3] mt-1">System specifications</p>
                </div>
                <div className="p-6">
                  <div data-tour="si-overview" className="grid grid-cols-3 gap-6">
                    {plantOverview.cards.map((card, i) => {
                      const Icon = iconMap[(card.icon as keyof typeof iconMap)] || Gauge;
                      return (
                        <motion.div
                          key={i}
                          variants={tileVariants}
                          initial="hidden"
                          animate="show"
                          transition={{ delay: i * 0.1 }}
                          className="border border-[#C2DFF1] p-4 hover:border-[#8AC4E6] hover:bg-[#F2F8FC] transition-all duration-200 rounded-lg"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <Icon className="w-5 h-5 text-[#0066A3]" />
                            <h3 className="font-semibold text-gray-900 text-sm">{card.title}</h3>
                          </div>
                          <div className="space-y-1.5">
                            {card.lines.slice(0, 3).map((line, idx) => (
                              <p key={idx} className="text-xs text-gray-600 leading-relaxed">
                                {line}
                              </p>
                            ))}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.section>
          )}

          {/* PAGE 2: Clean, concise table - dynamically sized */}
          {page === 1 && (
            <motion.section
              key="page-2"
              ref={refs[1]}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="h-[calc(100vh-120px)]"
            >
              <div className="h-full bg-white flex flex-col">
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-white to-[#F2F8FC]">
                  <h2 className="text-lg font-semibold text-gray-900">{technicalSpecifications.tableTitle}</h2>
                </div>

                <div className="flex-1 p-4 overflow-hidden">
                  <div className="h-full overflow-auto bg-gradient-to-br from-gray-50 to-[#F2F8FC] rounded-lg border border-[#E3F0F9]">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-[#0084CF] to-[#0066A3] text-white sticky top-0">
                        <tr>
                          <th className="text-left py-3 px-4 font-medium text-sm w-10">S.No</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Parameter</th>
                          <th className="text-left py-3 px-4 font-medium text-sm w-32 lg:w-40">Value</th>
                          <th className="text-left py-3 px-4 font-medium text-sm w-64 lg:w-80 xl:w-80">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E3F0F9]">
                        {technicalSpecifications.data.map((row, idx) => {
                          const [sno, param, qty, make] = row;
                          const sep = [sno, param, qty, make].every(v => v === "");
                          if (sep) return (
                            <tr key={`sep-${idx}`} className="h-2">
                              <td colSpan={4}>
                                <div className="h-px bg-gradient-to-r from-transparent via-[#C2DFF1] to-transparent" />
                              </td>
                            </tr>
                          );

                          const isSubItem = /[a-e]/i.test(sno);

                          return (
                            <tr key={`${sno}-${idx}`} className="hover:bg-[#F2F8FC] transition-colors">
                              <td className="py-2 px-4 text-sm text-center">
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded text-xs font-bold ${isSubItem
                                  ? 'bg-green-100 text-green-700 border border-green-200'
                                  : 'bg-[#E3F0F9] text-[#1C4D88] border border-[#C2DFF1]'
                                  }`}>
                                  {sno}
                                </span>
                              </td>
                              <td className={`py-2 px-4 text-sm ${isSubItem ? 'text-gray-600 pl-8' : 'text-gray-900 font-medium'}`}>
                                {param}
                              </td>
                              <td className="py-2 px-4 text-sm text-gray-900 font-medium">
                                {qty}
                              </td>
                              <td className="py-2 px-4 text-sm text-gray-600">
                                {make}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SiteInfo;
